/* eslint-disable max-len */
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { SealOfBiliteracyFileListItemModel } from 'src/types/SealOfBiliteracyFileListItemModel';
import { assessmentModel } from 'src/types/SealOfBiliteracyFormViewModel';
import { createSealOfBiliteracyValidationSchema } from 'src/types/Validation/SealOfBiliteracyValidationSchema';

interface SealOfBiliteracyContextType {
  selectedEnglishAssessment: string;
  selectedWorldLanguageAssessment: string;
  setSelectedEnglishAssessment: (assessment: string) => void;
  setSelectedWorldLanguageAssessment: (assessment: string) => void;
  currentEnglishAssessment?: assessmentModel;
  currentWorldLanguageAssessment?: assessmentModel;
  setCurrentEnglishAssessment: (assessment?: assessmentModel) => void;
  setCurrentWorldLanguageAssessment: (assessment?: assessmentModel) => void;
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  dynamicValidationSchema: any;
  // Document upload requirement tracking
  hasScoreEdits: boolean;
  setHasScoreEdits: (hasEdits: boolean) => void;
  isPortfolioAssessment: boolean;
  setIsPortfolioAssessment: (isPortfolio: boolean) => void;
  requiresDocumentUpload: boolean;
  // Student files for validation
  studentFiles: SealOfBiliteracyFileListItemModel[];
  setStudentFiles: (files: SealOfBiliteracyFileListItemModel[]) => void;
  // Track newly uploaded documents after score edits
  newlyUploadedFiles: SealOfBiliteracyFileListItemModel[];
  setNewlyUploadedFiles: (files: SealOfBiliteracyFileListItemModel[]) => void;
  // Form mode tracking
  isEditMode: boolean;
  setIsEditMode: (isEdit: boolean) => void;
  // Form reset function
  resetFormState: () => void;
}

const SealOfBiliteracyContext = createContext<
  SealOfBiliteracyContextType | undefined
>(undefined);

interface SealOfBiliteracyProviderProps {
  children: ReactNode;
}

export const SealOfBiliteracyProvider: React.FC<
  SealOfBiliteracyProviderProps
> = ({ children }) => {
  const [selectedEnglishAssessment, setSelectedEnglishAssessment] =
    useState<string>('');
  const [selectedWorldLanguageAssessment, setSelectedWorldLanguageAssessment] =
    useState<string>('');
  const [currentEnglishAssessment, setCurrentEnglishAssessment] = useState<
    assessmentModel | undefined
  >();
  const [currentWorldLanguageAssessment, setCurrentWorldLanguageAssessment] =
    useState<assessmentModel | undefined>();

  // Document upload requirement tracking
  const [hasScoreEdits, setHasScoreEdits] = useState<boolean>(false);
  const [isPortfolioAssessment, setIsPortfolioAssessment] =
    useState<boolean>(false);
  const [studentFiles, setStudentFiles] = useState<
    SealOfBiliteracyFileListItemModel[]
  >([]);
  const [newlyUploadedFiles, setNewlyUploadedFiles] = useState<
    SealOfBiliteracyFileListItemModel[]
  >([]);
  // Form mode tracking
  const [isEditMode, setIsEditMode] = useState<boolean>(false);

  // Memoize setStudentFiles to prevent unnecessary re-renders
  const memoizedSetStudentFiles = useCallback(
    (files: SealOfBiliteracyFileListItemModel[]) => {
      setStudentFiles(files);
    },
    []
  );

  // Memoize setNewlyUploadedFiles to prevent unnecessary re-renders
  const memoizedSetNewlyUploadedFiles = useCallback(
    (files: SealOfBiliteracyFileListItemModel[]) => {
      setNewlyUploadedFiles(files);
    },
    []
  );

  // Form reset function to clear all form state after successful save
  const resetFormState = useCallback(() => {
    setHasScoreEdits(false);
    setNewlyUploadedFiles([]);
    // Note: We don't reset studentFiles as they should persist
    // Note: We don't reset assessment selections as they should persist
  }, []);

  // Calculate if document upload is required
  // In edit mode: require upload if scores were edited OR portfolio assessment is selected
  // In create mode: require upload ONLY if portfolio assessment is selected
  const requiresDocumentUpload = isEditMode
    ? hasScoreEdits || isPortfolioAssessment
    : isPortfolioAssessment;

  // Create dynamic validation schema based on current assessments
  // Only recalculate when assessments or requirements change, not when files change
  const dynamicValidationSchema = useMemo(() => {
    return createSealOfBiliteracyValidationSchema(
      currentEnglishAssessment,
      currentWorldLanguageAssessment,
      requiresDocumentUpload,
      studentFiles,
      newlyUploadedFiles,
      hasScoreEdits,
      isEditMode
    );
  }, [
    currentEnglishAssessment,
    currentWorldLanguageAssessment,
    requiresDocumentUpload,
    // Keep studentFiles and newlyUploadedFiles in dependencies but optimize the validation logic
    studentFiles,
    newlyUploadedFiles,
    hasScoreEdits,
    isEditMode,
  ]);

  const value: SealOfBiliteracyContextType = {
    selectedEnglishAssessment,
    selectedWorldLanguageAssessment,
    setSelectedEnglishAssessment,
    setSelectedWorldLanguageAssessment,
    currentEnglishAssessment,
    currentWorldLanguageAssessment,
    setCurrentEnglishAssessment,
    setCurrentWorldLanguageAssessment,
    dynamicValidationSchema,
    hasScoreEdits,
    setHasScoreEdits,
    isPortfolioAssessment,
    setIsPortfolioAssessment,
    requiresDocumentUpload,
    studentFiles,
    setStudentFiles: memoizedSetStudentFiles,
    newlyUploadedFiles,
    setNewlyUploadedFiles: memoizedSetNewlyUploadedFiles,
    isEditMode,
    setIsEditMode,
    resetFormState,
  };

  return (
    <SealOfBiliteracyContext.Provider value={value}>
      {children}
    </SealOfBiliteracyContext.Provider>
  );
};

export const useSealOfBiliteracyContext = (): SealOfBiliteracyContextType => {
  const context = useContext(SealOfBiliteracyContext);
  if (context === undefined) {
    throw new Error(
      'useSealOfBiliteracyContext must be used within a SealOfBiliteracyProvider'
    );
  }
  return context;
};
